import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Simonsays.css";
import useUserInfo from "../../../Hooks/useUserInfo";
import { useAudio } from "../../../Providers/AudioProvider";
import Task from "../Task";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Badge,
} from "react-bootstrap";
import { FaQuestionCircle, FaPlay, FaRedo } from "react-icons/fa";
import { userGameRewards } from "../../../apis/user";
import logo from "../../../assets/images/coinlogo.png";

function Simonsays() {
  const { userDetails, updateUserInfo, updatewatchScreenInfo, watchScreen } =
    useUserInfo();
  const playClick = useAudio();
  const [sequence, setSequence] = useState([]);
  const [playerSequence, setPlayerSequence] = useState([]);
  const [gameState, setGameState] = useState("idle"); // "idle", "playing", "showing-sequence", "game-over", "restarting"
  const [score, setScore] = useState(0);
  const [finalScore, setFinalScore] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);
  const [displayNumber, setDisplayNumber] = useState(null);
  const [apiCallMade, setApiCallMade] = useState(false);
  const [isClickable, setIsClickable] = useState(true);
  const timeoutsRef = useRef([]);
  const sequenceTimerRef = useRef(null);
const basePoints = 10;
  const colors = ["red", "blue", "green", "yellow"];

  const IsPointsReached =
    watchScreen?.LoginMessage ===
    "You have already reached the maximum rewards limit"
      ? true
      : false;

  const calculatePoints = (finalScore) => {
    // Only award points if score is at least 10
    if (finalScore >= 30) {
      return 5000;
    } else if (finalScore >= 20) {
      return 3000;
    } else if (finalScore >= basePoints) {
      return 1000;
    }
    return 0;
  };

  // Define handleApiResponse with useCallback before it's used in the useEffect
  const handleApiResponse = useCallback(async (points) => {
    // Set flag to indicate API call is being made
    setApiCallMade(true);
    
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const apiData = {
        gamePoints: points,
        gameType: "simonsays",
      };
      const res = await userGameRewards(apiData);
      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));
        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));
      }
    } catch (error) {
      console.error("API call failed:", error);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, [updateUserInfo, updatewatchScreenInfo]);

  // Add an effect to ensure numbers are cleared from buttons when game state changes
  useEffect(() => {
    if (gameState !== "playing" && gameState !== "showing-sequence") {
      setDisplayNumber(null);
    }
  }, [gameState]);

  // Clean up any timers when component unmounts
  useEffect(() => {
    return () => {
      if (sequenceTimerRef.current) {
        clearTimeout(sequenceTimerRef.current);
      }
      // Clear all timeouts on unmount
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  // API call effect for rewarding points when game is over
  useEffect(() => {
    const handleGameRewards = async () => {
      if (gameState === "game-over" && finalScore >= basePoints && !IsPointsReached && !apiCallMade) {
        const points = calculatePoints(finalScore);
        await handleApiResponse(points);
      }
    };
    
    handleGameRewards();
  }, [gameState, finalScore, IsPointsReached, apiCallMade, handleApiResponse]);

  // Comprehensive function to completely reset all game state
  const completeReset = () => {
    console.log("Performing complete game state reset");

    // Reset all game-related state variables
    setSequence([]);
    setPlayerSequence([]);
    setScore(0);
    setFinalScore(0);
    setGameState("idle");
    setDisplayNumber(null);
    setApiCallMade(false);
    // Reset clickable state
    setIsClickable(true);

    // Clear any existing timers
    if (sequenceTimerRef.current) {
      clearTimeout(sequenceTimerRef.current);
      sequenceTimerRef.current = null;
    }
    
    // Clear all timeouts
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  };

  const closePopUp = () => {
    playClick();
    
    // Perform a complete reset before starting a new game
    completeReset();
    
    // Start a completely new game after a short delay
    const timeout = setTimeout(() => {
      startGame();
    }, 500);
    timeoutsRef.current.push(timeout);
  };

  const exitToMenu = () => {
    playClick();
    
    // Check if user has sufficient score to earn points and API call hasn't been made yet
    if (score >= basePoints && !IsPointsReached && !apiCallMade) {
      // Calculate points based on current score
      const points = calculatePoints(score);
      console.log(`Exiting with score ${score} - awarding ${points} points`);
      
      // Call API to award points before exiting to menu
      handleApiResponse(points).then(() => {
        // After API call completes, reset and return to task menu
        completeReset();
        updateUserInfo((prev) => ({
          ...prev,
          isPlay: false,
          currentComponent: Task,
          currentComponentText: "task",
          lastComponent: userDetails?.currentComponent,
          lastComponentText: userDetails?.currentComponentText,
          isMenu: true,
          menuCount: userDetails?.menuCount + 1,
        }));
      });
    } else {
      // If score is too low or points limit reached or API already called, just reset and exit
      completeReset();
      updateUserInfo((prev) => ({
        ...prev,
        isPlay: false,
        currentComponent: Task,
        currentComponentText: "task",
        lastComponent: userDetails?.currentComponent,
        lastComponentText: userDetails?.currentComponentText,
        isMenu: true,
        menuCount: userDetails?.menuCount + 1,
      }));
    }
  };

  const handleStartClick = () => {
    playClick();
    
    if (gameState === "game-over") {
      // If restarting after game over, show loading state for 2 seconds
      setGameState("restarting");

      const timeout = setTimeout(() => {
        // After delay, start the game
        startGame();
      }, 2000);
      timeoutsRef.current.push(timeout);
    } else {
      // If first time starting, start immediately
      startGame();
    }
  };

  const startGame = () => {
    console.log("Starting new game");

    // Initially set game state to playing but disable clicks until sequence is shown
    setGameState("playing");
    setIsClickable(false);

    // Create a new sequence with just one color to start
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    const newSequence = [randomColor];
    console.log("Initial sequence:", newSequence);

    // Set the sequence and then play it
    setSequence(newSequence);

    // Start showing the sequence after a delay to ensure player is ready
    const timeout = setTimeout(() => {
      playSequence(newSequence);
    }, 1500);
    timeoutsRef.current.push(timeout);
  };

  const addToSequence = () => {
    // Disable clicking immediately when adding to sequence
    setIsClickable(false);
    
    // Add a new random color to the existing sequence
    const randomColor = colors[Math.floor(Math.random() * colors.length)];

    // Important: Create a new sequence by adding to the existing one, not replacing it
    const newSequence = [...sequence, randomColor];
    console.log(
      "New sequence length:",
      newSequence.length,
      "colors:",
      newSequence
    );

    // Update the sequence with the new, longer sequence
    setSequence(newSequence);

    // Start showing the sequence after a delay
    const timeout = setTimeout(() => {
      playSequence(newSequence);
    }, 500);
    timeoutsRef.current.push(timeout);
  };

  const playSequence = (sequenceToPlay) => {
    setGameState("showing-sequence");
    // Disable clicking while sequence is showing
    setIsClickable(false);
    console.log("Playing sequence with length:", sequenceToPlay.length);

    // Clear any existing timers
    if (sequenceTimerRef.current) {
      clearTimeout(sequenceTimerRef.current);
    }

    let step = 0;

    const showNextStep = () => {
      if (step < sequenceToPlay.length) {
        console.log("Showing step", step + 1, "color:", sequenceToPlay[step]);

        // Show the number on the correct colored button
        setDisplayNumber({
          color: sequenceToPlay[step],
          number: step + 1,
        });

        // Show the number for a longer time to make it more visible
        sequenceTimerRef.current = setTimeout(() => {
          setDisplayNumber(null);

          // Wait before showing the next number
          sequenceTimerRef.current = setTimeout(() => {
            step++;
            showNextStep();
          }, 400);
        }, 800);
      } else {
        // Sequence finished playing
        console.log("Sequence finished playing");
        
        // Add a slight delay before allowing player to click
        // This prevents accidental rapid clicking right after sequence shows
        sequenceTimerRef.current = setTimeout(() => {
          setGameState("playing");
          // Re-enable clicking only after a short delay when sequence is fully completed
          setIsClickable(true);
        }, 500);
      }
    };

    // Start the sequence with a slight delay
    showNextStep();
  };

  const handleColorClick = (color) => {
    // Check if clicks are currently allowed and game is in playing state
    if (!isClickable || gameState !== "playing") return;
    
    playClick();
    
    // Temporarily disable clicking to prevent rapid successive clicks
    setIsClickable(false);
    
    // Visual feedback - show the step number the player is currently on
    const stepNumber = playerSequence.length + 1;
    setDisplayNumber({ color, number: stepNumber });

    const timeout = setTimeout(() => {
      setDisplayNumber(null);
    }, 300);
    timeoutsRef.current.push(timeout);

    // Add the clicked color to the player's sequence
    const newPlayerSequence = [...playerSequence, color];
    setPlayerSequence(newPlayerSequence);

    const currentIndex = newPlayerSequence.length - 1;
    console.log(
      `Player clicked ${color}, checking against position ${currentIndex} (${sequence[currentIndex]})`
    );

    // Check if the player made a mistake
    if (color !== sequence[currentIndex]) {
      console.log("Wrong color! Game over");
      // Game over - player made a mistake
      handleGameOver();
      return;
    }

    // Check if the player completed the current sequence
    if (newPlayerSequence.length === sequence.length) {
      console.log("Sequence complete! Score increased to", score + 1);
      // Player completed the current sequence successfully
      setScore((prev) => prev + 1);

      // Reset player sequence for the next round
      setPlayerSequence([]);

      // Add a new color to the sequence after a delay
      const timeout = setTimeout(() => {
        addToSequence();
      }, 1000);
      timeoutsRef.current.push(timeout);
    } else {
      // Re-enable clicks after a short delay to prevent rapid clicking
      // but still maintain good game responsiveness
      const timeout = setTimeout(() => {
        setIsClickable(true);
      }, 300);
      timeoutsRef.current.push(timeout);
    }
  };

  const handleGameOver = () => {
    console.log("Game over triggered");

    // Store the final score before resetting it
    const currentScore = score;
    setFinalScore(currentScore);

    // Disable clicking when game is over
    setIsClickable(false);
    setGameState("game-over");
    setPlayerSequence([]);
    
    // Ensure no numbers are displayed on the buttons
    setDisplayNumber(null);

    // Clear any existing timers
    if (sequenceTimerRef.current) {
      clearTimeout(sequenceTimerRef.current);
      sequenceTimerRef.current = null;
    }
  };

  return (
    <Container
      fluid
      className="simon-game-container p-0"
      style={{ pointerEvents: "fill", backgroundColor: "#000000" }}
    >
      <Row className="m-0">
        <Col className="p-0">
          <div className="game-header-simon d-flex justify-content-between align-items-center p-2">
            <img
              variant="link"
              className="cancel-button"
              onClick={exitToMenu}
              src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
              style={{ width: "25px" }}
              alt="Cancel"
            />
            <div className="d-flex align-items-center">
              <Card className="score-card" style={{ width: "100%" }}>
                <Card.Body className="p-2">
                 
                </Card.Body>
              </Card>
              <Button
                style={{ color: "#03bb14" }}
                variant="link"
                className="help-button ms-2"
                onClick={() => {
                  playClick();
                  setShowInstructions(!showInstructions);
                }}
              >
                <FaQuestionCircle size={24} />
              </Button>
            </div>
          </div>

          <Modal
            style={{ maxHeight: "100vh" ,background:'black',maxWidth:'100vw'}}
            show={showInstructions}
            onHide={() => setShowInstructions(false)}
            centered
            className="instructions-modal"
          >
            <Modal.Header>
               {/* style={{color:'#bd72ff'}}>HOW TO PLAY */}
                               <h4 style={{
                  marginTop:'10px',
                  background: 'linear-gradient(180deg, #FD8D23 -1.34%, #FAE61C 10.31%, #EEF73B 20.95%, #4CF038 42.23%, #33C6F6 65.04%, #7475FF 80.74%, #D457FF 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>HOW TO PLAY</h4>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowInstructions(false)}
                style={{
                  background: `url('https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png') no-repeat center center`,
                  backgroundSize: '24px 24px',
                  width: '24px',
                  height: '28px',
                  border: 'none',
                  opacity: '1',
                  padding: '0',
                  pointerEvents:'fill',
                  zIndex:'1000',
                }}
              >
              </button>
            </Modal.Header>
            <Modal.Body>
              <div className="instructions-content">
                <ol className="instruction-list" style={{ fontSize: "0.9rem" }}>
                  <li>CLICK "START GAME" TO BEGIN</li>
                  <li>WATCH THE NUMBERED SEQUENCE PATTERN THAT APPEARS</li>
                  <li>
                    REPEAT THE SEQUENCE BY CLICKING THE BOXES IN THE SAME ORDER
                  </li>
                  <li>IF YOU MAKE A MISTAKE, THE GAME ENDS</li>
                  <li>
                    IF YOU COMPLETE THE SEQUENCE CORRECTLY, THE SCORE INCREASES
                    AND A NEW NUMBER IS ADDED
                  </li>
                </ol>
                <div className="token-info">
                  <h5>TOKEN REWARDS:</h5>
                  <ul className="reward-list" style={{ fontSize: "0.9rem" }}>
                    <li>SCORE 10-19: GET 1000 $MEMETV TOKENS</li>
                    <li>SCORE 20-29: GET 3000 $MEMETV TOKENS</li>
                    <li>SCORE 30+: GET 5000 $MEMETV TOKENS</li>
                  </ul>
                </div>
              </div>
            </Modal.Body>
          </Modal>

          <Modal
            show={gameState === "game-over" && !IsPointsReached}
            onHide={() => {}}
            centered
            className="game-over-modal"
            backdrop="static"
          >
            <Modal.Body className="text-center">
              <h3 className="press-start">GAME OVER!</h3>
              <p
                className="text-uppercase text-danger"
                style={{ fontSize: "1rem" }}
              >
                YOU MISSED THE SEQUENCE
              </p>
              <p>YOUR SCORE: {finalScore}</p>
              <div className="token-reward">
                <img src={logo} alt="Token" className="token-icon" />
                <p style={{ marginBottom: "0px" }}>
                  {finalScore < basePoints
                    ? 0
                    : finalScore < 20
                    ? 1000
                    : finalScore < 30
                    ? 3000
                    : 5000}
                </p>
              </div>
              <div className="d-flex justify-content-center gap-3 mt-3">
                <Button variant="primary" onClick={closePopUp}>
                  <FaPlay className="me-2" />
                  PLAY AGAIN
                </Button>
                <Button variant="secondary" onClick={exitToMenu}>
                  EXIT
                </Button>
              </div>
            </Modal.Body>
          </Modal>

          <Modal
            show={gameState === "game-over" && IsPointsReached}
            onHide={() => {}}
            centered
            className="limit-modal"
          >
            <Modal.Body className="text-center">
              <h3>DAILY LIMIT REACHED</h3>
              <p>YOU'VE REACHED THE MAXIMUM REWARDS LIMIT FOR TODAY</p>
              <Button variant="secondary" onClick={exitToMenu} className="mt-3">
                EXIT TO GAMES
              </Button>
            </Modal.Body>
          </Modal>

          <div className="game-board-simon">

          <h5 className="" style={{textAlign:'center', display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <p>SCORE: {score}</p>
                  </h5>
            <div className="simon-circle ">
              <div className="simon-center">
                <div className="simon-logo text-center">
                  SIMON <br />
                  SAYS
                </div>
              </div>
              <div className="simon-quadrants">
                {colors.map((color) => (
                  <div key={color} className={`simon-quadrant ${color}`}>
                    <Button
                      id={color}
                      className={`color-button ${color} ${
                        displayNumber && displayNumber.color === color
                          ? "active"
                          : ""
                      }`}
                      onClick={() => handleColorClick(color)}
                      disabled={gameState !== "playing"}
                      style={{border: 'none',margin:'0'}}
                    >
                      {displayNumber && displayNumber.color === color && (
                        <span className="button-number">
                          {displayNumber.number}
                        </span>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div className="start-button-container flex justify-content-center" style={{marginTop: '30px'}}>
              {gameState === "idle" && (
                <div className="start-button-container flex justify-content-center">
                  <Button
                    size="lg"
                    onClick={handleStartClick}
                    className="start-button"
                  >
                    <FaPlay className="me-2" />
                    START GAME
                  </Button>
                </div>
              )}
              {gameState === "restarting" && (
                <div className="start-button-container flex justify-content-center">
                  <Button
                    size="lg"
                    disabled
                    className="start-button"
                  >
                    <FaRedo className="me-2" />
                    RESTARTING...
                  </Button>
                </div>
              )}
              {gameState === "showing-sequence" && (
                <div className="sequence-message" style={{marginTop: '20px'}}>
                  <h5>WATCH THE SEQUENCE... ({sequence.length})</h5>
                </div>
              )}
            </div>
            
            {/* Exit Game Button */}
            {(gameState === "playing" || gameState === "showing-sequence") && (
              <div className="exit-button-container mt-4 d-flex justify-content-center">
                <Button
                  onClick={exitToMenu}
                  className="start-button"
                >
                  EXIT GAME
                </Button>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default Simonsays;